import React, { useState, useRef, useCallback } from 'react';
import styled from 'styled-components';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 8px;
  border: 1px solid var(--card-border);
  cursor: col-resize;
  user-select: none;
`;

const BaseImg = styled.img`
  display: block;
  width: 100%;
  height: auto;
`;

const OverlayImg = styled.img<{ clip: number }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  clip-path: inset(0 ${p => 100 - p.clip}% 0 0);
`;

const Divider = styled.div<{ pos: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${p => p.pos}%;
  width: 3px;
  background: #7dd3fc;
  transform: translateX(-50%);
  pointer-events: none;
`;

const Handle = styled.div<{ pos: number; visible: boolean }>`
  position: absolute;
  top: 50%;
  left: ${p => p.pos}%;
  transform: translate(-50%, -50%);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #7dd3fc;
  box-shadow: 0 2px 12px rgba(125,211,252,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  color: #0a0a0a;
  opacity: ${p => p.visible ? 1 : 0};
  transition: opacity 0.3s ease;
`;

const Label = styled.span<{ side: 'left' | 'right' }>`
  position: absolute;
  bottom: 10px;
  ${p => p.side === 'left' ? 'left: 12px' : 'right: 12px'};
  font-size: 13px;
  font-family: 'JetBrains Mono', monospace;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #fff;
  background: rgba(0,0,0,0.5);
  padding: 2px 6px;
  border-radius: 3px;
  pointer-events: none;
`;

interface Props {
  leftSrc: string;
  rightSrc: string;
  leftLabel?: string;
  rightLabel?: string;
  alt?: string;
}

const ImageCompare: React.FC<Props> = ({ leftSrc, rightSrc, leftLabel, rightLabel, alt }) => {
  const [pos, setPos] = useState(35);
  const [handleVisible, setHandleVisible] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const updatePos = useCallback((clientX: number) => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const pct = Math.min(Math.max((clientX - rect.left) / rect.width * 100, 0), 100);
    setPos(pct);
  }, []);

  const onMouseDown = (e: React.MouseEvent) => {
    dragging.current = true;
    updatePos(e.clientX);
    const onMove = (e: MouseEvent) => { if (dragging.current) updatePos(e.clientX); };
    const onUp = () => { dragging.current = false; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const onTouchMove = (e: React.TouchEvent) => updatePos(e.touches[0].clientX);

  return (
    <Wrapper
      ref={wrapperRef}
      onMouseDown={onMouseDown}
      onMouseEnter={() => setHandleVisible(true)}
      onMouseLeave={() => setHandleVisible(false)}
      onTouchMove={onTouchMove}
    >
      <BaseImg src={rightSrc} alt={alt ?? ''} draggable={false} />
      <OverlayImg src={leftSrc} alt="" clip={pos} draggable={false} />
      <Divider pos={pos} />
      <Handle pos={pos} visible={handleVisible}>
        <svg width="28" height="16" viewBox="0 0 28 16" fill="none">
          <polygon points="0,8 9,0 9,16" fill="#0f4c75" />
          <polygon points="28,8 19,0 19,16" fill="#0f4c75" />
        </svg>
      </Handle>
      {leftLabel && <Label side="left">{leftLabel}</Label>}
      {rightLabel && <Label side="right">{rightLabel}</Label>}
    </Wrapper>
  );
};

export default ImageCompare;
